import EmailIcon from "@mui/icons-material/Email";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import {
  Box,
  Container,
  Grid,
  Link as MuiLink,
  Stack,
  SvgIcon,
  type SvgIconProps,
  Typography,
  styled,
} from "@mui/material";

import {
  APP_STORE_BADGE,
  PLATFORM_WEBSITE,
  PLAY_STORE_BADGE,
  POWERED_BY_LOGO,
} from "@/constants/images";
import { ROUTE_DIGITAL_MENU, ROUTE_HOME, ROUTE_MENU } from "@/constants/routes";
import { DRAWER_VIEW } from "@/constants/views";
import useIsWebView from "@/hooks/useIsWebView";
import { useAppDispatch, useAppSelector } from "@/store";
import { openDrawer } from "@/store/slices/commonSlice";

/*
 * Recovered from the original's own `MenuBody` chunk — `recovery-source/
 * live-chunks/mod-9802.js`, component `rJ` with styled parts `r$` (root),
 * `r0` (column), `r1` (heading), `r2` (contact row), `r3` (web-view bar),
 * `r4` (social button), `r5` (bottom bar), `r7` (bare link), `r9` (link
 * button).
 *
 * Three things this reconstruction had wrong, all of them visible:
 *
 *  1. The root broke out to `width: 100vw` with a `calc(50% - 50vw)` pull.
 *     The footer renders INSIDE the menu page's left scroll column (the
 *     original mounts `rJ` inside `iL`, `flex:1; overflowY:auto`), so a
 *     viewport-width band is wider than its own container: it overhung the
 *     cart panel and the column's `overflowX: hidden` sheared its right-hand
 *     edge off — the reported "Orde… / Takea… / Delive…" clipping. The
 *     original's root has no breakout at all, just `marginTop: auto`.
 *  2. There were FOUR columns. The original has THREE (`md={4}` each), and
 *     the fourth — an invented "Order / Takeaway / Delivery" column — was the
 *     one being cut off. Removed.
 *  3. The third column is Policies OR "Download Our App", never both: the
 *     original swaps Policies out when the tenant has a store URL.
 */

const Root = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(6, 0, 3),
  // Holds the footer at the bottom of the scroll column on short pages
  // instead of letting it float up under the last category.
  marginTop: "auto",
}));

const Column = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
});

const ColumnHeading = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(1),
  color: theme.palette.primary.contrastText,
}));

const ContactRow = styled(Stack)({
  flexDirection: "row",
  alignItems: "flex-start",
  gap: "8px",
  "& svg": { marginTop: "2px" },
});

const BottomBar = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginTop: theme.spacing(4),
  paddingTop: theme.spacing(3),
  borderTop: `1px solid ${theme.palette.primary.contrastText}33`,
  flexWrap: "wrap",
  gap: theme.spacing(2),
}));

const SocialButton = styled(MuiLink)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 36,
  height: 36,
  borderRadius: "50%",
  backgroundColor: `${theme.palette.primary.contrastText}1A`,
  color: theme.palette.primary.contrastText,
  transition: "background-color 0.2s ease",
  "&:hover": { backgroundColor: `${theme.palette.primary.contrastText}33` },
}));

const WebViewBar = styled(Stack)(({ theme }) => ({
  padding: "7.5px 0",
  backgroundColor: theme.palette.primary.main,
  position: "relative",
})) as typeof Stack;

const BareLink = styled(MuiLink)({
  textDecoration: "none",
  display: "inline-block",
  lineHeight: 0,
});

const BrandImage = styled("img")({ display: "block" });

/*
 * The original's `r9` is a plain `Box` with an onClick — visually a link, but
 * unreachable by keyboard. Rendering it as a reset `button` is byte-for-byte
 * identical on screen and restores tab focus; the styles below are the
 * original's, plus the reset that removes the UA button chrome.
 */
const LinkButton = styled(Box)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  cursor: "pointer",
  "&:hover": { textDecoration: "underline" },
  background: "none",
  border: "none",
  padding: 0,
  font: "inherit",
  textAlign: "left",
})) as typeof Box;

/** X (Twitter). The original inlines this path — it is not a MUI icon. */
const XIcon = (props: SvgIconProps) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </SvgIcon>
);

/** TikTok. Also inlined by the original. */
const TikTokIcon = (props: SvgIconProps) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </SvgIcon>
);

/**
 * Site footer. Every field is the tenant's own: a row is omitted when the
 * tenant has not filled it in, which is why butt-karahi-main — whose
 * `restaurant_address`, `public_phone_numbers` and social links are all empty
 * — correctly shows only its logo, name and support e-mail.
 */
export default function SiteFooter() {
  const dispatch = useAppDispatch();
  const isStaff = useAppSelector((state) => state.auth.isStaff);
  const isWebView = useIsWebView();
  const {
    playstore_url: playstoreUrl,
    appstore_url: appstoreUrl,
    restaurant_address: address,
    public_business_name: publicBusinessName,
    name,
    support_email: email,
    public_phone_numbers: phones,
    image_url: imageUrl,
    digital_menu_images: digitalMenuImages,
    fb_link: facebook,
    instagram_link: instagram,
    twitter_link: twitter,
    tiktok_link: tiktok,
  } = useAppSelector((state) => state.settings.data);

  const businessName = publicBusinessName || name;
  const hasPlaystore = Boolean(playstoreUrl);
  const hasAppstore = Boolean(appstoreUrl);
  const hasApp = hasPlaystore || hasAppstore;

  // Staff use this same storefront to take orders; the original hides the
  // whole footer from them rather than restyling it.
  if (isStaff) return null;

  // Inside the React Native shell the app chrome supplies the navigation, so
  // the footer collapses to the platform badge alone.
  if (isWebView) {
    return (
      <WebViewBar
        component="footer"
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="center"
      >
        <BrandImage alt="Provided By Starasoft" src={POWERED_BY_LOGO} height={25} />
      </WebViewBar>
    );
  }

  const quickLinkDrawers = [
    { label: "About", view: DRAWER_VIEW.ABOUT },
    { label: "Opening Hours", view: DRAWER_VIEW.STORE_SCHEDULE },
    { label: "Delivery Areas", view: DRAWER_VIEW.DELIVERY_AREAS },
    { label: "Reviews", view: DRAWER_VIEW.REVIEWS },
  ];

  const policyLinks = [
    { label: "Terms & Conditions", view: DRAWER_VIEW.TERMS_N_CONDITIONS },
    { label: "Privacy Policy", view: DRAWER_VIEW.PRIVACY_POLICY },
    { label: "Refund Policy", view: DRAWER_VIEW.REFUND_POLICY },
    { label: "Shipping Policy", view: DRAWER_VIEW.SHIPPING_POLICY },
    { label: "Cancellation Policy", view: DRAWER_VIEW.CANCELLATION_POLICY },
  ];

  const socials = [
    { href: facebook, label: "Facebook", Icon: FacebookIcon },
    { href: instagram, label: "Instagram", Icon: InstagramIcon },
    { href: twitter, label: "X (Twitter)", Icon: XIcon },
    { href: tiktok, label: "TikTok", Icon: TikTokIcon },
  ].filter((social) => Boolean(social.href));

  return (
    <Root component="footer">
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Column>
              {imageUrl && (
                <Box sx={{ mb: 2 }}>
                  <BrandImage
                    src={imageUrl}
                    alt={businessName}
                    sx={{
                      maxWidth: "150px",
                      maxHeight: "80px",
                      objectFit: "contain",
                      borderRadius: "12px",
                    }}
                  />
                </Box>
              )}
              <ColumnHeading variant="h6">{businessName}</ColumnHeading>
              {address && (
                <ContactRow>
                  <LocationOnIcon fontSize="small" />
                  <Typography variant="body2">{address}</Typography>
                </ContactRow>
              )}
              {phones && phones.length > 0 && (
                <ContactRow>
                  <PhoneIcon fontSize="small" />
                  <MuiLink
                    href={`tel:${phones[0]}`}
                    color="inherit"
                    underline="hover"
                  >
                    <Typography variant="body2">{phones[0]}</Typography>
                  </MuiLink>
                </ContactRow>
              )}
              {email && (
                <ContactRow>
                  <EmailIcon fontSize="small" />
                  <MuiLink
                    href={`mailto:${email}`}
                    color="inherit"
                    underline="hover"
                  >
                    <Typography variant="body2">{email}</Typography>
                  </MuiLink>
                </ContactRow>
              )}
              {socials.length > 0 && (
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  {socials.map(({ href, label, Icon }) => (
                    <SocialButton
                      key={label}
                      href={href as string}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={label}
                    >
                      <Icon fontSize="small" />
                    </SocialButton>
                  ))}
                </Stack>
              )}
            </Column>
          </Grid>

          <Grid item xs={12} md={4}>
            <Column>
              <ColumnHeading variant="h6">Quick Links</ColumnHeading>
              <Stack spacing={1}>
                <MuiLink href={ROUTE_HOME} color="inherit" underline="hover">
                  <Typography variant="body2">Home</Typography>
                </MuiLink>
                <MuiLink href={ROUTE_MENU} color="inherit" underline="hover">
                  <Typography variant="body2">Menu</Typography>
                </MuiLink>
                {digitalMenuImages && digitalMenuImages.length > 0 && (
                  <MuiLink
                    href={ROUTE_DIGITAL_MENU}
                    color="inherit"
                    underline="hover"
                  >
                    <Typography variant="body2">Digital Menu</Typography>
                  </MuiLink>
                )}
                {quickLinkDrawers.map(({ label, view }) => (
                  <LinkButton
                    key={label}
                    component="button"
                    type="button"
                    onClick={() => dispatch(openDrawer(view))}
                  >
                    <Typography variant="body2">{label}</Typography>
                  </LinkButton>
                ))}
              </Stack>
            </Column>
          </Grid>

          {!hasApp && (
            <Grid item xs={12} md={4}>
              <Column>
                <ColumnHeading variant="h6">Policies</ColumnHeading>
                <Stack spacing={1}>
                  {policyLinks.map(({ label, view }) => (
                    <LinkButton
                      key={label}
                      component="button"
                      type="button"
                      onClick={() => dispatch(openDrawer(view))}
                    >
                      <Typography variant="body2">{label}</Typography>
                    </LinkButton>
                  ))}
                </Stack>
              </Column>
            </Grid>
          )}

          {hasApp && (
            <Grid item xs={12} md={4}>
              <Column>
                <ColumnHeading variant="h6">Download Our App</ColumnHeading>
                <Stack spacing={1.5} alignItems="flex-start">
                  {hasPlaystore && (
                    <BareLink
                      href={playstoreUrl ?? ""}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <BrandImage
                        src={PLAY_STORE_BADGE}
                        alt="Playstore"
                        width={135}
                        height={40}
                      />
                    </BareLink>
                  )}
                  {hasAppstore && (
                    <BareLink
                      href={appstoreUrl ?? ""}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <BrandImage
                        src={APP_STORE_BADGE}
                        alt="Appstore"
                        width={135}
                        height={40}
                      />
                    </BareLink>
                  )}
                </Stack>
              </Column>
            </Grid>
          )}
        </Grid>

        <BottomBar>
          <Typography variant="body2" color="inherit">
            © {new Date().getFullYear()} {businessName}. All rights reserved.
          </Typography>
          <BareLink href={PLATFORM_WEBSITE} target="_blank" rel="noreferrer">
            <BrandImage src={POWERED_BY_LOGO} alt="Provided By Starasoft" height={22} />
          </BareLink>
        </BottomBar>
      </Container>
    </Root>
  );
}
